import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Bed, Plus, Edit2, Trash2, Loader2, Save, X, AlertTriangle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BedTypeManagement() {
  const [bedTypes, setBedTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBedType, setEditingBedType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingBedType, setDeletingBedType] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchBedTypes();
  }, []);

  const fetchBedTypes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/bed-types?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBedTypes(res.data);
    } catch (error) {
      toast.error('Failed to load bed types');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBedType(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (bedType) => {
    setEditingBedType(bedType);
    setFormData({
      name: bedType.name || '',
      description: bedType.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Bed type name is required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingBedType) {
        // Update
        await axios.put(`${API_URL}/api/bed-types/${editingBedType.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bed type updated successfully');
      } else {
        // Create
        await axios.post(`${API_URL}/api/bed-types`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bed type created successfully');
      }
      
      setDialogOpen(false);
      fetchBedTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bed type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingBedType) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/bed-types/${deletingBedType.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bed type deleted successfully');
      setDeleteConfirmOpen(false);
      setDeletingBedType(null);
      fetchBedTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bed type');
    }
  };

  const toggleActive = async (bedType) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/bed-types/${bedType.id}`, {
        is_active: !bedType.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Bed type ${bedType.is_active ? 'deactivated' : 'activated'}`);
      fetchBedTypes();
    } catch (error) {
      toast.error('Failed to update bed type status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="bed-type-management">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Bed className="h-7 w-7 text-indigo-500" />
            Bed Type Management
          </h1>
          <p className="text-gray-500 mt-1">Define bed types available in your hotel rooms</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-bed-type-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Bed Type
        </Button>
      </div>

      {/* Bed Types Grid */}
      {bedTypes.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Bed className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Bed Types Defined</h3>
            <p className="text-gray-400 mb-4">Create bed types to assign to rooms (e.g., Single, Double, King, Queen)</p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Bed Type
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bedTypes.map((bedType) => (
            <Card 
              key={bedType.id} 
              className={`hover:shadow-md transition-shadow ${!bedType.is_active ? 'opacity-60 bg-gray-50' : ''}`}
              data-testid={`bed-type-card-${bedType.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Bed className="h-5 w-5 text-indigo-500" />
                    <CardTitle className="text-lg">{bedType.name}</CardTitle>
                  </div>
                  <Badge variant={bedType.is_active ? "default" : "secondary"}>
                    {bedType.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {bedType.description && (
                  <p className="text-sm text-gray-500 mb-4">{bedType.description}</p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Active</span>
                    <Switch 
                      checked={bedType.is_active} 
                      onCheckedChange={() => toggleActive(bedType)}
                      data-testid={`toggle-${bedType.id}`}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(bedType)}
                      data-testid={`edit-${bedType.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => {
                        setDeletingBedType(bedType);
                        setDeleteConfirmOpen(true);
                      }}
                      data-testid={`delete-${bedType.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bed className="h-5 w-5 text-indigo-500" />
              {editingBedType ? 'Edit Bed Type' : 'Create Bed Type'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Bed Type Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., King Size, Queen Size, Single"
                className="mt-1"
                data-testid="bed-type-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
                className="mt-1"
                rows={3}
                data-testid="bed-type-description-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-bed-type-btn">
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingBedType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" />
              Delete Bed Type?
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{deletingBedType?.name}</strong>? 
            This will deactivate the bed type. Rooms using this bed type will not be affected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
